import {
  Box,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  useDisclosure,
  ThemingProps,
} from '@chakra-ui/react'
import React, {
  ComponentProps,
  FC,
  createContext,
  useCallback,
  useMemo,
} from 'react'

type THeaderPopoverContext = {
  forceClose: () => void
}

export const HeaderPopoverContext = createContext<THeaderPopoverContext>({
  forceClose: () => {
    /* noop */
  },
})

interface IHeaderPopoverProps extends Pick<PopoverProps, 'onClose' | 'onOpen'> {
  trigger: ComponentProps<typeof PopoverTrigger>['children']
  width?: ThemingProps['size']
  bodyHeight?: ThemingProps['size']
  children?: React.ReactNode,
  'data-testid'?: string
}

/**
 * Popover for the header menu.
 *
 * The component uses the containment strategy with multiple holes.
 * See: https://reactjs.org/docs/composition-vs-inheritance.html#containment
 *
 * Uses the `trigger` as a an opener.
 *
 * Should be combined with the `HeaderPopoverList`, `HeaderPopoverListItem`
 * and `HeaderPopoverListDivider` components.
 */
export const HeaderPopover: FC<IHeaderPopoverProps> = ({
  children,
  onClose,
  onOpen,
  trigger,
  width,
  bodyHeight,
}) => {
  const {
    isOpen,
    onClose: onClosePopover,
    onOpen: onOpenPopover,
  } = useDisclosure()

  const handleOnClose = useCallback(() => {
    onClosePopover()
    onClose?.()
  }, [onClose, onClosePopover])

  const handleOnOpen = useCallback(() => {
    onOpenPopover()
    onOpen?.()
  }, [onOpen, onOpenPopover])

  const value = useMemo(
    () => ({
      forceClose: handleOnClose,
    }),
    [handleOnClose],
  )

  return (
    <HeaderPopoverContext.Provider value={value}>
      <Popover isOpen={isOpen} onClose={handleOnClose} onOpen={handleOnOpen}>
        <PopoverTrigger>
          <Box cursor="pointer">{trigger}</Box>
        </PopoverTrigger>
        <PopoverContent
          data-testid="header-popover"
          marginRight={5}
          marginTop={0}
          width={width ?? 'xs'}>
          <PopoverBody
            height={bodyHeight ?? 'auto'}
            overflow="hidden"
            p={4}>
            {children}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </HeaderPopoverContext.Provider>
  )
}
